import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { dismiss, notifyError, notifyProblems } from '../lib/errorNotices';
import { LoadFailed } from '../components/QueryState';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { canAuthorAutomations } from '@promanage/core';
import {
  availableFields,
  TEMPLATE_CHANNELS,
  TEMPLATE_CHANNEL_LABELS,
  TEMPLATE_EXAMPLE_VALUES,
  TEMPLATE_RECORD_TYPES,
  TEMPLATE_RECORD_TYPE_LABELS,
  CORRESPONDENCE_STARTERS,
  renderTemplate,
  type TemplateChannel,
  type TemplatePurpose,
  type TemplateRecordType,
} from '@promanage/documents/template';
import { Button, Chip, Field, Input, Panel, PanelHeader, Select } from '@promanage/ui';
import { Icon } from '../components/Icon';
import { TopBar } from '../components/TopBar';
import { SettingsNav } from '../features/settings/SettingsNav';
import { useSession } from '../features/auth/session';
import { FilterChips, ListShell } from '../features/table/ListShell';
import { DataTable } from '../features/table/DataTable';
import { type ColumnDef, type SortState } from '../features/table/columns';
import { downloadDocument } from '../features/reports/downloadDocument';
import { whenText } from '../features/automations/format';
import { letterBrand } from '../features/correspondence/letterBrand';
import { jobState } from '../features/correspondence/jobState';
import { MergeFieldPicker } from '../features/correspondence/MergeFieldPicker';
import { RecordPicker, type RecordHit } from '../features/correspondence/RecordPicker';
import { trpc, type RouterOutputs } from '../lib/trpc';
import { SaveButton, useUnsavedChanges } from '../components/UnsavedChanges';
import { FormDialog } from '../components/FormDialog';
export const Route = createFileRoute('/settings/templates')({ component: TemplatesPage });

const STATUS_FILTERS = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
] as const;
const PREVIEW_SOURCES = [
  { value: 'example', label: 'Example data' },
  { value: 'record', label: 'A record' },
] as const;
type TemplateRow = RouterOutputs['correspondence']['list']['rows'][number];
type Draft = {
  id?: string;
  revision?: number;
  name: string;
  subject: string;
  body: string;
  recordType: TemplateRecordType;
  channel: TemplateChannel;
  requiresApproval: boolean;
  noticeKind: NoticeKind | null;
  /** A starter's warning about its wording. Shown while editing; never saved. */
  caution?: string;
};
type NoticeKind = 'reminder' | 'notice_to_remedy';
const EMPTY: Draft = {
  name: '',
  subject: '',
  body: '',
  recordType: 'any',
  channel: 'any',
  requiresApproval: false,
  noticeKind: null,
};
type SavedTemplate = Pick<
  TemplateRow,
  | 'id'
  | 'revision'
  | 'name'
  | 'subject'
  | 'body'
  | 'recordType'
  | 'channel'
  | 'requiresApproval'
  | 'noticeKind'
>;
const draftOf = (row: SavedTemplate): Draft => ({
  id: row.id,
  revision: row.revision,
  name: row.name,
  subject: row.subject,
  body: row.body,
  recordType: row.recordType,
  channel: row.channel,
  requiresApproval: row.requiresApproval,
  noticeKind: row.noticeKind,
});
/** Whether the screen shows exactly what was last saved — publishing acts on the SAVED revision. */
const unsaved = (draft: Draft, saved: SavedTemplate | undefined) =>
  !saved ||
  draft.revision !== saved.revision ||
  draft.name !== saved.name ||
  draft.subject !== saved.subject ||
  draft.body !== saved.body ||
  draft.recordType !== saved.recordType ||
  draft.channel !== saved.channel ||
  draft.requiresApproval !== saved.requiresApproval ||
  draft.noticeKind !== saved.noticeKind;
const STATUS_LABELS: Readonly<Record<TemplateRow['status'], string>> = {
  unpublished: 'Not published',
  published: 'Published',
  changes: 'Changes not published',
  awaiting_approval: 'Awaiting approval',
};
const STATUS_TONES: Readonly<
  Record<TemplateRow['status'], 'neutral' | 'positive' | 'warn' | 'accent'>
> = {
  unpublished: 'neutral',
  published: 'positive',
  changes: 'warn',
  awaiting_approval: 'accent',
};
const NOTICE_LABELS: Readonly<Record<NoticeKind, string>> = {
  reminder: 'A reminder',
  notice_to_remedy: 'A notice to remedy',
};
const contentOf = (draft: Draft) => ({
  subject: draft.subject,
  body: draft.body,
  recordType: draft.recordType,
  channel: draft.channel,
});

/** One notice for the sample's own problems, replaced rather than stacked on a second try. */
const SAMPLE_PROBLEM = 'template-sample';
function TemplatesPage() {
  const form = useUnsavedChanges();
  const mayAuthor = canAuthorAutomations(useSession()?.roles ?? []);
  const client = useQueryClient();
  const [search, setSearch] = useState('');
  const [archived, setArchived] = useState(false);
  const [offset, setOffset] = useState(0);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [picking, setPicking] = useState(false);
  const [sort, setSort] = useState<SortState<'name' | 'subject' | 'revision'>>({
    column: 'name',
    direction: 'asc',
  });
  const list = useQuery(
    trpc.correspondence.list.queryOptions(
      {
        search,
        archived,
        offset,
        sort: sort.column,
        direction: sort.direction,
      },
      { meta: { errorTitle: 'Could not load the letter templates' } },
    ),
  );
  const jobs = useQuery(
    trpc.correspondence.jobs.queryOptions(undefined, {
      meta: { errorTitle: 'Could not load production history' },
    }),
  );
  const invalidate = () => {
    void client.invalidateQueries({ queryKey: trpc.correspondence.list.queryKey() });
    void client.invalidateQueries({ queryKey: trpc.correspondence.get.queryKey() });
    void client.invalidateQueries({ queryKey: trpc.correspondence.history.queryKey() });
  };
  const save = useMutation(
    trpc.correspondence.save.mutationOptions({
      meta: { errorTitle: 'Could not save the template' },
      // STAY IN THE EDITOR. Saving writes a draft; the next thing an author does is publish it.
      onSuccess: (row) => {
        form.saved();
        invalidate();
        setDraft((current) => ({
          ...draftOf(row),
          ...(current?.caution ? { caution: current.caution } : {}),
        }));
      },
    }),
  );
  const retry = useMutation(
    trpc.correspondence.retry.mutationOptions({
      meta: { errorTitle: 'Could not retry production' },
      onSuccess: () => {
        void jobs.refetch();
      },
    }),
  );
  const archive = useMutation(
    trpc.correspondence.archive.mutationOptions({
      meta: { errorTitle: 'Could not archive that template' },
      onSuccess: invalidate,
    }),
  );
  const columns: ColumnDef<'name' | 'subject' | 'revision', TemplateRow>[] = [
    {
      id: 'name',
      label: 'Template',
      width: '36%',
      fixed: true,
      defaultVisible: true,
      sortValue: (row) => row.name,
      render: (row) => (
        <div className="space-y-1">
          <button
            className="text-left font-semibold text-accent hover:underline"
            onClick={() => {
              save.reset();
              setDraft(draftOf(row));
            }}
          >
            {row.name}
          </button>
          <p className="flex flex-wrap items-center gap-1.5 text-[0.75rem] text-ink-muted">
            <Chip tone={STATUS_TONES[row.status]}>{STATUS_LABELS[row.status]}</Chip>
            {TEMPLATE_RECORD_TYPE_LABELS[row.recordType]} · {TEMPLATE_CHANNEL_LABELS[row.channel]}
            {row.requiresApproval ? ' · needs approval' : ''}
          </p>
          <div className="flex gap-3 text-[0.75rem]">
            {mayAuthor ? (
              <>
                <button
                  className="text-ink-muted hover:underline"
                  onClick={() => {
                    setArchived(false);
                    const { id: _id, revision: _revision, ...copy } = draftOf(row);
                    setDraft({ ...copy, name: `${row.name} copy`.slice(0, 120) });
                  }}
                >
                  Duplicate
                </button>
                <button
                  disabled={archive.isPending}
                  className="text-ink-muted hover:underline"
                  onClick={() =>
                    archive.mutate({ id: row.id, revision: row.revision, archived: !archived })
                  }
                >
                  {archived ? 'Restore' : 'Archive'}
                </button>
              </>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      id: 'subject',
      label: 'Subject / document title',
      width: '48%',
      defaultVisible: true,
      sortValue: (row) => row.subject,
      render: (row) => row.subject,
    },
    {
      id: 'revision',
      label: 'Revision',
      width: '16%',
      defaultVisible: true,
      sortValue: (row) => row.revision,
      render: (row) => row.revision,
    },
  ];
  const rows = list.data?.rows ?? [];
  const recentlyProduced = (
    <Panel className="overflow-hidden">
      <PanelHeader
        title="Recently produced"
        subtitle="The latest 50 letters"
        aside={
          <Button
            size="sm"
            variant="secondary"
            disabled={jobs.isFetching}
            onClick={() => void jobs.refetch()}
          >
            Refresh
          </Button>
        }
      />
      {jobs.isError ? (
        <LoadFailed inline retry={() => void jobs.refetch()} retrying={jobs.isFetching} />
      ) : null}
      {jobs.data?.length === 0 ? (
        <p className="border-t border-rule px-5 py-4 text-[0.8125rem] text-ink-muted">
          Nothing produced yet.
        </p>
      ) : (
        <ul className="divide-y divide-rule border-t border-rule">
          {jobs.data?.map((job) => (
            <li key={job.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-[0.8125rem] font-semibold">{job.title}</p>
                <p className="text-[0.75rem] text-ink-muted">
                  {whenText(job.createdAt)} · {jobState(job)} · rev {job.templateRevision} ·{' '}
                  {job.runId ? 'Automation' : (job.requestedBy ?? 'A user')}
                </p>
                {job.lastError ? (
                  <p className="text-[0.75rem] text-danger">{job.lastError}</p>
                ) : null}
              </div>
              {job.completedAt ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    void downloadDocument(client, job.id).catch((cause: unknown) => {
                      notifyError(cause, 'Could not download that document');
                    });
                  }}
                >
                  Download PDF
                </Button>
              ) : mayAuthor && job.attempts > 0 ? (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={retry.isPending}
                  onClick={() => retry.mutate({ id: job.id })}
                >
                  Retry
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
  const startFrom = (next: Draft) => {
    setArchived(false);
    save.reset();
    setPicking(false);
    setDraft(next);
  };

  if (draft)
    return (
      <TemplateEditor
        draft={draft}
        setDraft={(next) => {
          form.touch();
          setDraft(next);
        }}
        mayAuthor={mayAuthor}
        readOnly={archived && !!draft.id}
        pending={save.isPending}
        justSaved={form.justSaved}
        onSave={() => save.mutate(draft)}
        onBack={() => {
          form.reset();
          setDraft(null);
        }}
      />
    );

  return (
    <>
      <ListShell
        title="Letter templates"
        subtitle="Wording for letters and emails"
        nav={<SettingsNav />}
        actions={
          mayAuthor ? (
            <Button size="sm" onClick={() => setPicking(true)}>
              Add template
            </Button>
          ) : null
        }
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setOffset(0);
        }}
        searchPlaceholder="Find a template…"
        searchLabel="Search templates"
        filters={
          <FilterChips
            label="Template status"
            value={archived ? 'archived' : 'active'}
            options={STATUS_FILTERS}
            onChange={(value) => {
              setArchived(value === 'archived');
              setOffset(0);
            }}
          />
        }
        trailing={
          offset > 0 || list.data?.hasMore ? (
            <div className="flex items-center gap-2">
              <span className="text-[0.75rem] text-ink-muted">
                Page {Math.floor(offset / 50) + 1}
              </span>
              <Button
                size="sm"
                variant="secondary"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - 50))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={!list.data?.hasMore}
                onClick={() => setOffset(offset + 50)}
              >
                Next
              </Button>
            </div>
          ) : null
        }
        isPending={list.isPending}
        error={list.error}
        onRetry={() => void list.refetch()}
        isEmpty={rows.length === 0}
        empty={
          <div className="p-6">
            <p className="font-semibold">No templates found</p>
            <p className="mt-1 text-[0.8125rem] text-ink-muted">
              {search || archived ? 'Try another search or filter.' : 'Add one to get started.'}
            </p>
          </div>
        }
        below={recentlyProduced}
      >
        <DataTable
          rows={rows}
          columns={columns}
          sort={sort}
          onSortChange={(value) => {
            setSort(value);
            setOffset(0);
          }}
          rowKey={(row) => row.id}
          caption="Letter templates, current page"
          minWidth="40rem"
        />
      </ListShell>

      {picking ? (
        <StarterDialog
          onClose={() => setPicking(false)}
          onBlank={() => startFrom(EMPTY)}
          onStarter={(starter) =>
            startFrom({
              ...EMPTY,
              ...starter,
              requiresApproval: starter.requiresApproval ?? false,
              noticeKind: starter.noticeKind ?? null,
            })
          }
        />
      ) : null}
    </>
  );
}

type Starter =(typeof CORRESPONDENCE_STARTERS)[number];

/**
 * Blank, or one of the suggested wordings. It used to be a panel of its own under the list, always
 * on screen for a choice made once per template; it lives behind "Add template" now, the way the
 * automation page's starting points do. Nothing is saved until the author saves.
 */
function StarterDialog({
  onClose,
  onBlank,
  onStarter,
}: {
  onClose: () => void;
  onBlank: () => void;
  onStarter: (starter: Starter) => void;
}) {
  const option =
    'flex w-full flex-col items-start gap-1 rounded-md bg-surface-sunk px-3 py-2.5 text-left hover:bg-accent-soft focus-visible:ring-2 focus-visible:ring-accent';
  return (
    <FormDialog title="Add a template" onClose={onClose} onSubmit={(e) => e.preventDefault()}>
      <button type="button" className={option} onClick={onBlank}>
        <span className="text-[0.8125rem] font-semibold">Blank template</span>
        <span className="text-[0.75rem] text-ink-muted">Start from nothing</span>
      </button>
      <p className="mt-1 text-[0.75rem] font-semibold text-ink-muted">Or start from</p>
      <ul className="grid gap-2">
        {CORRESPONDENCE_STARTERS.map((starter) => (
          <li key={starter.name}>
            <button type="button" className={option} onClick={() => onStarter(starter)}>
              <span className="text-[0.8125rem] font-semibold">{starter.name}</span>
              <span className="flex flex-wrap items-center gap-1.5 text-[0.75rem] text-ink-muted">
                {TEMPLATE_RECORD_TYPE_LABELS[starter.recordType]} ·{' '}
                {TEMPLATE_CHANNEL_LABELS[starter.channel]}
                {starter.caution ? <Chip tone="warn">Legal wording</Chip> : null}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <div>
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </FormDialog>
  );
}

function TemplateEditor({
  draft,
  setDraft,
  mayAuthor,
  readOnly,
  pending,
  justSaved,
  onSave,
  onBack,
}: {
  draft: Draft;
  setDraft: (draft: Draft) => void;
  mayAuthor: boolean;
  readOnly: boolean;
  pending: boolean;
  justSaved: boolean;
  onSave: () => void;
  onBack: () => void;
}) {
  const formId = useId();
  const body = useRef<HTMLTextAreaElement>(null);
  const preview = renderTemplate(contentOf(draft), TEMPLATE_EXAMPLE_VALUES);
  const fields = availableFields(draft.recordType, draft.channel);
  const locked = pending || !mayAuthor || readOnly;

  /** At the caret, not the end: a field is nearly always wanted mid-sentence. */
  const insert = (text: string) => {
    const area = body.current;
    const at = area?.selectionStart ?? draft.body.length;
    const to = area?.selectionEnd ?? at;
    setDraft({ ...draft, body: `${draft.body.slice(0, at)}${text}${draft.body.slice(to)}` });
    requestAnimationFrame(() => {
      area?.focus();
      area?.setSelectionRange(at + text.length, at + text.length);
    });
  };

  const samplePdf = async () => {
    const letter = renderTemplate(contentOf(draft), TEMPLATE_EXAMPLE_VALUES, {
      purpose: 'letter',
    });
    if (!letter.ok) {
      notifyProblems('Could not build the sample', letter.issues, SAMPLE_PROBLEM);
      return;
    }
    try {
      const [{ buildTemplatePdf }, brand] = await Promise.all([
        import('@promanage/documents/template-pdf'),
        letterBrand(TEMPLATE_EXAMPLE_VALUES['agency.email']),
      ]);
      const bytes = buildTemplatePdf({
        agencyName: TEMPLATE_EXAMPLE_VALUES['agency.name'] ?? '',
        title: letter.subject,
        body: letter.body,
        date: TEMPLATE_EXAMPLE_VALUES.date ?? '',
        brand,
      });
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template-sample.pdf';
      a.click();
      URL.revokeObjectURL(url);
      dismiss(SAMPLE_PROBLEM);
    } catch (cause) {
      notifyError(cause, 'Could not build the sample PDF', { id: SAMPLE_PROBLEM });
    }
  };

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      {/*
       * SAVE LIVES IN THE TOP BAR, which stays in view, and reaches the form by its id. At the foot
       * of the page it sat below a sixteen-row editor and a preview, a scroll away from the words.
       */}
      <TopBar
        title={draft.name || 'New template'}
        subtitle={
          draft.id
            ? `Revision ${String(draft.revision ?? 1)} · ${TEMPLATE_RECORD_TYPE_LABELS[draft.recordType]}`
            : 'Not saved yet'
        }
        actions={
          <>
            <Button size="sm" variant="secondary" disabled={pending} onClick={onBack}>
              All templates
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={!preview.ok || draft.channel === 'email'}
              onClick={() => void samplePdf()}
            >
              Sample PDF
            </Button>
            {mayAuthor && !readOnly ? (
              <SaveButton
                size="sm"
                form={formId}
                pending={pending}
                saved={justSaved}
                disabled={!draft.name.trim() || !preview.ok}
              >
                Save
              </SaveButton>
            ) : null}
          </>
        }
      />
      <SettingsNav />

      <form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault();
          if (mayAuthor && preview.ok && !pending && !readOnly) onSave();
        }}
        className="flex flex-col gap-4"
      >
        {readOnly ? (
          <Notice title="Archived">
            Restore it from the list to edit it or use it in automations.
          </Notice>
        ) : null}
        {draft.caution ? <Notice title="Legal wording">{draft.caution}</Notice> : null}

        {/* `min-w-0` on each fieldset: a fieldset's min-content width otherwise lets a long body
            line push the grid wider than the page. */}
        <fieldset disabled={locked} className="min-w-0">
          <Panel className="p-5">
            <PanelHeader title="Details" className="p-0 pb-3" />
            <div className="grid gap-4 md:grid-cols-3">
              <Field label="Name" required>
                {(props) => (
                  <Input
                    {...props}
                    maxLength={120}
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  />
                )}
              </Field>
              <Field label="About" required hint="Decides which fields it can use">
                {(props) => (
                  <Select
                    {...props}
                    value={draft.recordType}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        recordType: e.target.value as TemplateRecordType,
                        noticeKind: e.target.value === 'tenancy' ? draft.noticeKind : null,
                      })
                    }
                  >
                    {TEMPLATE_RECORD_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {TEMPLATE_RECORD_TYPE_LABELS[type]}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Sent as" required>
                {(props) => (
                  <Select
                    {...props}
                    value={draft.channel}
                    onChange={(e) =>
                      setDraft({ ...draft, channel: e.target.value as TemplateChannel })
                    }
                  >
                    {TEMPLATE_CHANNELS.map((channel) => (
                      <option key={channel} value={channel}>
                        {TEMPLATE_CHANNEL_LABELS[channel]}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              {draft.recordType === 'tenancy' ? (
                <Field label="Is a notice" hint="Offers to record service when produced">
                  {(props) => (
                    <Select
                      {...props}
                      value={draft.noticeKind ?? ''}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          noticeKind: e.target.value === '' ? null : (e.target.value as NoticeKind),
                        })
                      }
                    >
                      <option value="">No</option>
                      {(Object.keys(NOTICE_LABELS) as NoticeKind[]).map((kind) => (
                        <option key={kind} value={kind}>
                          {NOTICE_LABELS[kind]}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              ) : null}
              {/*
               * Switching this off in a draft still needs approval while the PUBLISHED wording asks
               * for it — the server's rule, so an author cannot talk their way round it.
               */}
              <label className="flex items-start gap-2 self-end pb-2 text-[0.8125rem] md:col-span-2">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 shrink-0 accent-[var(--color-accent)]"
                  checked={draft.requiresApproval}
                  onChange={(e) => setDraft({ ...draft, requiresApproval: e.target.checked })}
                />
                <span>
                  A second administrator approves publishing
                  <span className="block text-[0.75rem] text-ink-muted">For legal wording</span>
                </span>
              </label>
            </div>
          </Panel>
        </fieldset>

        {/* Side by side from lg: the preview is what the wording is judged by, so it stays in view
            while typing rather than a scroll away. */}
        <div className="grid gap-4 lg:grid-cols-2">
          <fieldset disabled={locked} className="min-w-0">
            <Panel className="flex h-full flex-col gap-4 p-5">
              <PanelHeader title="Wording" className="p-0" />
              <Field label="Subject / title" required>
                {(props) => (
                  <Input
                    {...props}
                    maxLength={200}
                    value={draft.subject}
                    onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                  />
                )}
              </Field>
              <Field label="Body" required hint="Plain text. A blank line starts a new paragraph.">
                {(props) => (
                  <div className="rounded-md ring-1 ring-rule focus-within:ring-accent">
                    <div className="rounded-t-md border-b border-rule bg-surface-sunk p-2">
                      <div className="max-w-sm">
                        <MergeFieldPicker
                          fields={fields}
                          onInsert={(key) => insert(`{{${key}}}`)}
                        />
                      </div>
                    </div>
                    <textarea
                      {...props}
                      ref={body}
                      rows={16}
                      maxLength={20000}
                      className="block w-full resize-y rounded-b-md bg-surface p-3 font-mono text-[0.8125rem] outline-none"
                      value={draft.body}
                      onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                    />
                  </div>
                )}
              </Field>
              <details className="group text-[0.75rem] text-ink-muted">
                <summary className="flex w-fit cursor-pointer list-none items-center gap-1 font-semibold text-ink-muted hover:text-ink">
                  <Icon
                    name="chevron-right"
                    size={14}
                    className="transition-transform duration-[var(--duration-fast)] group-open:rotate-90"
                  />
                  Fallbacks and conditions
                </summary>
                {/* A field with neither holds the automation rather than sending a blank. */}
                <dl className="mt-2 grid gap-x-3 gap-y-1.5 pl-5 sm:grid-cols-[auto_1fr]">
                  <dt className="font-semibold text-ink">Fallback</dt>
                  <dd>
                    <code>{'{{tenancy.tenants | "there"}}'}</code>
                  </dd>
                  <dt className="font-semibold text-ink">Only if present</dt>
                  <dd>
                    <code>{'{{#if arrears.days}}…{{else}}…{{/if}}'}</code>
                  </dd>
                </dl>
              </details>
            </Panel>
          </fieldset>
          <DraftPreview draft={draft} preview={preview} />
        </div>

        {draft.id ? (
          <div className="grid items-start gap-4 lg:grid-cols-2">
            <Publication draft={draft} mayAuthor={mayAuthor} readOnly={readOnly} />
            <RevisionHistory
              templateId={draft.id}
              current={draft.revision}
              mayRestore={mayAuthor && !readOnly && !pending}
              onUse={(wording) => setDraft({ ...draft, ...wording })}
            />
          </div>
        ) : null}
      </form>
    </div>
  );
}

function Notice({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div role="note" className="rounded-md border-l-4 border-l-warn bg-surface-sunk px-4 py-3">
      <p className="text-[0.8125rem] font-semibold">{title}</p>
      <p className="mt-0.5 text-[0.8125rem] text-ink-muted">{children}</p>
    </div>
  );
}

type Rendered = ReturnType<typeof renderTemplate>;

/**
 * The draft rendered two ways in one place. Example data proves the template is well formed; a
 * real record proves the RECORD can fill it — the missing-field problem an automation would
 * otherwise discover at the moment it held. One card, because both answer "what will this say?".
 *
 * The record render follows the draft after typing settles, and keeps
 * the last result on screen while the next one loads so the pane does not flash empty per keystroke.
 */
function DraftPreview({ draft, preview }: { draft: Draft; preview: Rendered }) {
  const [source, setSource] = useState<'example' | 'record'>('example');
  const [record, setRecord] = useState<RecordHit | null>(null);
  const [recipientName, setRecipientName] = useState('');
  const [purpose, setPurpose] = useState<TemplatePurpose>('letter');
  const effectivePurpose: TemplatePurpose =
    draft.channel === 'any' ? purpose : draft.channel === 'email' ? 'email' : 'letter';
  const wording = useMemo(
    () => ({ content: contentOf(draft), recipientName: recipientName.trim() }),
    [draft, recipientName],
  );
  const [settledWording, setSettledWording] = useState(wording);
  useEffect(() => {
    // Rendering can query arrears and rent; keep typing within the shared API request budget.
    const timer = setTimeout(() => setSettledWording(wording), 400);
    return () => clearTimeout(timer);
  }, [wording]);
  const settling = wording !== settledWording;
  const result = useQuery(
    trpc.correspondence.preview.queryOptions(
      {
        content: settledWording.content,
        record: { type: record?.type ?? 'tenancy', id: record?.id ?? '' },
        purpose: effectivePurpose,
        ...(settledWording.recipientName ? { recipientName: settledWording.recipientName } : {}),
      },
      {
        meta: { errorTitle: 'Could not render against that record' },
        enabled: source === 'record' && preview.ok && record !== null && !settling,
        retry: false,
        placeholderData: (previous) => previous,
      },
    ),
  );

  return (
    <Panel className="flex min-w-0 flex-col gap-3 p-5">
      <PanelHeader
        className="p-0"
        title="Preview"
        subtitle={source === 'example' ? 'Example data' : 'A real record — nothing is sent'}
        aside={
          <FilterChips
            label="Preview with"
            value={source}
            options={PREVIEW_SOURCES}
            onChange={setSource}
          />
        }
      />
      {draft.subject.trim() === '' && draft.body.trim() === '' ? (
        // A blank draft is not an error yet; the issues appear once there is wording to judge.
        <p className="flex flex-1 items-center justify-center rounded-md border border-dashed border-rule p-5 text-[0.8125rem] text-ink-muted">
          Write a subject and body to see it here.
        </p>
      ) : !preview.ok ? (
        <ul role="alert" className="space-y-1 text-[0.8125rem] text-danger">
          {preview.issues.map((issue) => (
            <li key={issue}>{issue}</li>
          ))}
        </ul>
      ) : source === 'example' ? (
        <div className="flex-1 rounded-md bg-surface-sunk p-5">
          <h3 className="font-semibold">{preview.subject}</h3>
          <p className="mt-4 whitespace-pre-wrap break-words text-[0.8125rem]">{preview.body}</p>
        </div>
      ) : (
        <>
          {/* Labels are aria-only so the row stays one aligned line. */}
          <div className="flex flex-wrap gap-2">
            <RecordPicker recordType={draft.recordType} selected={record} onSelect={setRecord} />
            {draft.channel === 'any' ? (
              <Select
                aria-label="Render as"
                className="w-auto"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value as TemplatePurpose)}
              >
                <option value="letter">As a letter</option>
                <option value="email">As an email</option>
              </Select>
            ) : null}
            {effectivePurpose === 'email' ? (
              <Input
                aria-label="Recipient name"
                placeholder="Recipient name"
                className="w-44"
                value={recipientName}
                maxLength={120}
                onChange={(e) => setRecipientName(e.target.value)}
              />
            ) : null}
          </div>
          {result.isError ? (
            <LoadFailed inline retry={() => void result.refetch()} retrying={result.isFetching} />
          ) : result.data?.ok ? (
            <div
              className={`flex-1 rounded-md bg-surface-sunk p-5 transition-opacity duration-[var(--duration-fast)] ${result.isFetching || settling ? 'opacity-60' : ''}`}
            >
              <p className="text-[0.75rem] text-ink-muted">{result.data.recordLabel}</p>
              <h3 className="mt-1 font-semibold">{result.data.subject}</h3>
              <p className="mt-4 whitespace-pre-wrap break-words text-[0.8125rem]">
                {result.data.body}
              </p>
            </div>
          ) : result.data ? (
            <div role="alert" className="flex-1 rounded-md bg-danger-soft p-5 text-[0.8125rem]">
              <p className="font-semibold">
                {result.data.recordLabel ?? 'That record'} would hold this template:
              </p>
              <ul className="mt-1 space-y-1 text-danger">
                {result.data.issues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="flex flex-1 items-center justify-center rounded-md border border-dashed border-rule p-5 text-center text-[0.8125rem] text-ink-muted">
              {result.isFetching ? 'Rendering…' : 'Choose a record to fill the draft from.'}
            </div>
          )}
        </>
      )}
    </Panel>
  );
}

function RevisionHistory({
  templateId,
  current,
  mayRestore,
  onUse,
}: {
  templateId: string;
  current: number | undefined;
  mayRestore: boolean;
  onUse: (
    wording: Pick<
      Draft,
      'subject' | 'body' | 'recordType' | 'channel' | 'requiresApproval' | 'noticeKind'
    >,
  ) => void;
}) {
  const history = useQuery(
    trpc.correspondence.history.queryOptions(
      { id: templateId },
      { meta: { errorTitle: 'Could not load the history' } },
    ),
  );
  const template = useQuery(trpc.correspondence.get.queryOptions({ id: templateId }));
  const published = template.data?.publishedRevision ?? null;
  const [open, setOpen] = useState<number | null>(null);
  return (
    <Panel className="p-0">
      <PanelHeader
        title="Revision history"
        subtitle="Letters keep the wording they were produced with"
        className="px-5 pt-5 pb-3"
      />
      {history.isError ? (
        <LoadFailed inline retry={() => void history.refetch()} retrying={history.isFetching} />
      ) : null}
      <ul className="divide-y divide-rule">
        {history.data?.map((row) => (
          <li key={row.revision} className="px-5 py-3">
            <div className="flex flex-wrap items-center gap-2 text-[0.8125rem]">
              <button
                type="button"
                aria-expanded={open === row.revision}
                className="font-semibold text-accent hover:underline"
                onClick={() => setOpen(open === row.revision ? null : row.revision)}
              >
                Revision {row.revision}
              </button>
              <span className="text-[0.75rem] text-ink-muted">
                {whenText(row.createdAt)} · {row.createdBy ?? 'Recorded before history was kept'}
              </span>
              {row.revision === current ? <Chip tone="accent">Current draft</Chip> : null}
              {row.revision === published ? <Chip tone="positive">Published</Chip> : null}
              {row.archived ? <Chip tone="neutral">Archived</Chip> : null}
            </div>
            {open === row.revision ? (
              <div className="mt-2 rounded-md bg-surface-sunk p-4">
                <p className="text-[0.75rem] text-ink-muted">
                  {TEMPLATE_RECORD_TYPE_LABELS[row.recordType]} ·{' '}
                  {TEMPLATE_CHANNEL_LABELS[row.channel]}
                </p>
                <p className="mt-1 font-semibold">{row.subject}</p>
                <pre className="mt-2 whitespace-pre-wrap font-mono text-[0.75rem]">{row.body}</pre>
                {mayRestore && row.revision !== current ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="mt-3"
                    onClick={() =>
                      onUse({
                        subject: row.subject,
                        body: row.body,
                        recordType: row.recordType,
                        channel: row.channel,
                        requiresApproval: row.requiresApproval,
                        noticeKind: row.noticeKind,
                      })
                    }
                  >
                    Use this wording
                  </Button>
                ) : null}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/**
 * Where the template stands between what was saved and what goes out, and the one action that
 * moves it on — publish, approve, or withdraw a request.
 *
 * PUBLISHING ACTS ON THE SAVED REVISION, so the buttons wait while the screen holds unsaved edits:
 * a Publish button beside changed text reads as "publish what I see", and what would go out is the
 * last save.
 */
function Publication({
  draft,
  mayAuthor,
  readOnly,
}: {
  draft: Draft;
  mayAuthor: boolean;
  readOnly: boolean;
}) {
  const client = useQueryClient();
  const me = useSession()?.user.id;
  const id = draft.id ?? '';
  const template = useQuery(trpc.correspondence.get.queryOptions({ id }));
  const publications = useQuery(trpc.correspondence.publications.queryOptions({ id }));
  const refresh = () => {
    void client.invalidateQueries({ queryKey: trpc.correspondence.get.queryKey() });
    void client.invalidateQueries({ queryKey: trpc.correspondence.list.queryKey() });
    void client.invalidateQueries({ queryKey: trpc.correspondence.publications.queryKey() });
    void client.invalidateQueries({ queryKey: trpc.correspondence.history.queryKey() });
  };
  const publish = useMutation(
    trpc.correspondence.publish.mutationOptions({
      meta: { errorTitle: 'Could not publish that template' },
      onSuccess: refresh,
    }),
  );
  const approve = useMutation(
    trpc.correspondence.approve.mutationOptions({
      meta: { errorTitle: 'Could not approve that template' },
      onSuccess: refresh,
    }),
  );
  const withdraw = useMutation(
    trpc.correspondence.withdrawApproval.mutationOptions({
      meta: { errorTitle: 'Could not withdraw that template' },
      onSuccess: refresh,
    }),
  );
  const row = template.data;
  if (!row) return null;
  const dirty = unsaved(draft, row);
  const busy = publish.isPending || approve.isPending || withdraw.isPending;

  return (
    <Panel className="p-5">
      <PanelHeader
        className="p-0"
        title="Publishing"
        subtitle="Automations and letters use the published wording"
        aside={<Chip tone={STATUS_TONES[row.status]}>{STATUS_LABELS[row.status]}</Chip>}
      />
      <p className="mt-3 text-[0.8125rem]">
        {row.published
          ? `Revision ${String(row.published.revision)} is what goes out.`
          : 'Nothing goes out from this template yet.'}{' '}
        {row.status === 'awaiting_approval'
          ? `Revision ${String(row.approvalRequestedRevision)} awaits approval from another administrator (put forward by ${row.approvalRequestedBy ?? 'an administrator'}).`
          : null}
      </p>
      {dirty && !readOnly ? (
        <p className="mt-2 text-[0.8125rem] text-warn">
          Save first — publishing uses the saved draft.
        </p>
      ) : null}
      {mayAuthor && !readOnly ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {row.status === 'unpublished' || row.status === 'changes' ? (
            <Button
              type="button"
              disabled={dirty || busy}
              onClick={() => publish.mutate({ id, revision: row.revision })}
            >
              {row.requiresApproval || row.published?.requiresApproval
                ? 'Put forward for approval'
                : `Publish revision ${String(row.revision)}`}
            </Button>
          ) : null}
          {row.status === 'awaiting_approval' ? (
            <>
              <Button
                type="button"
                disabled={dirty || busy || row.approvalRequestedByUserId === me}
                onClick={() => approve.mutate({ id, revision: row.revision })}
              >
                Approve and publish
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => withdraw.mutate({ id })}
              >
                Withdraw request
              </Button>
            </>
          ) : null}
        </div>
      ) : null}
      {row.status === 'awaiting_approval' && row.approvalRequestedByUserId === me ? (
        <p className="mt-2 text-[0.75rem] text-ink-muted">
          You put this forward, so someone else approves it.
        </p>
      ) : null}
      {publications.data && publications.data.length > 0 ? (
        <ul className="mt-4 space-y-1 border-t border-rule pt-3 text-[0.75rem] text-ink-muted">
          {publications.data.map((entry) => (
            <li key={`${String(entry.revision)}-${entry.createdAt}`}>
              Revision {entry.revision} published {whenText(entry.createdAt)}
              {entry.requestedBy ? ` by ${entry.requestedBy}` : ' when publishing was introduced'}
              {entry.approvedBy ? `, approved by ${entry.approvedBy}` : ''}
            </li>
          ))}
        </ul>
      ) : null}
    </Panel>
  );
}
